import { pick } from 'src/i18n/contentLanguage';

/* The registration and join pages of a LONELINESS Circle, English first and
   Norwegian bokmål when the browser is Norwegian (no em-dashes). Product
   copy spells "Circle" with a capital C. */

export const JOIN_TITLE = pick('Join your Circle', 'Bli med i din Circle');
export const JOIN_TEXT = pick(
  'Enter the code you got from the leader. It has six characters.',
  'Skriv inn koden du fikk av lederen. Den har seks tegn.'
);
export const JOIN_CODE_LABEL = pick('Code', 'Kode');
export const JOIN_CODE_PLACEHOLDER = pick(
  'For example K7PX2M',
  'F.eks. K7PX2M'
);
export const JOIN_BUTTON_LABEL = pick('Continue', 'Fortsett');
export const JOIN_LOOKING_LABEL = pick(
  'Finding your Circle ...',
  'Finner din Circle ...'
);
export const JOIN_NOT_FOUND_TEXT = pick(
  'No Circle has this code. Check that you typed it right.',
  'Ingen Circle har denne koden. Sjekk at du skrev den riktig.'
);
export const JOIN_FAILED_TEXT = pick(
  'Something went wrong. Try again in a moment.',
  'Noe gikk galt. Prøv igjen om litt.'
);
export const JOIN_CODE_LENGTH = 6;

export const REGISTRATION_LOADING_TEXT = pick(
  'Loading the registration ...',
  'Henter registreringen ...'
);
export const REGISTRATION_UNAVAILABLE_TEXT = pick(
  'Registration is not available for this Circle right now.',
  'Registreringen er ikke tilgjengelig for denne Circlen akkurat nå.'
);
export const REGISTRATION_WRONG_KIND_TEXT = pick(
  'This Circle uses a different registration.',
  'Denne Circlen bruker en annen registrering.'
);

export const circleTitle = (name: string | null | undefined): string =>
  name?.trim() ? name.trim() : pick('Your Circle', 'Din Circle');
export const REGISTRATION_INTRO = pick(
  'Create an account with your phone number and you are in. It takes under a minute.',
  'Lag en konto med telefonnummeret ditt, så er du med. Det tar under ett minutt.'
);
export const SIGNED_IN_INTRO = (name: string): string =>
  pick(
    `You are signed in as ${name}. Confirm below and you are in.`,
    `Du er logget inn som ${name}. Bekreft under, så er du med.`
  );

export const FIRST_NAME_LABEL = pick('First name', 'Fornavn');
export const SURNAME_LABEL = pick('Surname', 'Etternavn');
export const PHONE_LABEL = pick('Phone number', 'Telefonnummer');
export const EMAIL_LABEL = pick('Email', 'E-post');
export const PASSWORD_LABEL = pick('Choose a password', 'Velg et passord');
export const PASSWORD_HINT = pick(
  'At least 8 characters, with at least one digit and one symbol. You sign in to the app with your phone number and this password.',
  'Minst 8 tegn, med minst ett tall og ett symbol. Du logger inn i appen med telefonnummeret og dette passordet.'
);

export const CONSENT_LABEL = pick(
  'I have read the privacy notice and understand that the institution offering the programme is responsible for my data, and that Dunbar148 processes it on their behalf.',
  'Jeg har lest personvernerklæringen og forstår at institusjonen som tilbyr programmet er ansvarlig for opplysningene mine, og at Dunbar148 behandler dem på deres vegne.'
);
export const PRIVACY_LINK_LABEL = pick(
  'Read the privacy notice',
  'Les personvernerklæringen'
);
export const PRIVACY_TITLE = pick(
  'Privacy, in short',
  'Personvern, kort fortalt'
);
export const PRIVACY_PARAGRAPHS: readonly string[] = pick(
  [
    'The institution offering the programme (for example the student welfare organisation or the university college) is responsible for your data. Dunbar148 processes it on the institution’s behalf, under a written agreement, and uses it for nothing else.',
    'We store your name, phone number and email, what you write in the group, and your answers to the three questions about loneliness. The answers to the questions are your own: the leader only sees one combined number for the group, and only once at least five have answered.',
    'The answers are used in research only if you say yes to it, without your name, and you can change your mind at any time under Measure.',
    'You can ask for access, correction or deletion by writing to the institution or to hello@dunbar148.com. The full privacy notice comes from the institution.',
  ],
  [
    'Institusjonen som tilbyr programmet (for eksempel studentsamskipnaden eller høyskolen) er ansvarlig for opplysningene dine. Dunbar148 behandler dem på institusjonens vegne, etter en skriftlig avtale, og bruker dem ikke til noe annet.',
    'Vi lagrer navn, telefonnummer og e-post, det du skriver i gruppen, og svarene dine på de tre spørsmålene om ensomhet. Svarene på spørsmålene er dine egne: lederen ser bare et samlet tall for gruppen, og bare når minst fem har svart.',
    'Svarene brukes i forskning bare hvis du sier ja til det, uten navn, og du kan ombestemme deg når som helst under Måling.',
    'Du kan be om innsyn, retting eller sletting ved å skrive til institusjonen eller til hello@dunbar148.com. Den fullstendige personvernerklæringen får du fra institusjonen.',
  ]
);
export const CLOSE_LABEL = pick('Close', 'Lukk');

export const CREATE_ACCOUNT_LABEL = pick('Create account', 'Opprett konto');
export const WORKING_LABEL = pick('Working ...', 'Arbeider ...');
export const JOIN_NOW_LABEL = pick('Join', 'Bli med');

export const CONFIRM_TITLE = pick(
  'Confirm your phone number',
  'Bekreft telefonnummeret'
);
export const CONFIRM_TEXT = (phone: string): string =>
  pick(
    `We sent a code by SMS to ${phone}. Enter it here.`,
    `Vi sendte en kode på SMS til ${phone}. Skriv den inn her.`
  );
export const CODE_LABEL = pick('Code from the SMS', 'Kode fra SMS');
export const CODE_PLACEHOLDER = pick('6 digits', '6 siffer');
export const RESEND_LABEL = pick('Send a new code', 'Send ny kode');
export const RESENT_TEXT = pick(
  'A new code has been sent by SMS.',
  'Ny kode er sendt på SMS.'
);
export const CONFIRM_LABEL = pick('Confirm and join', 'Bekreft og bli med');
export const BACK_LABEL = pick('Back', 'Tilbake');

export const SUCCESS_TEXT = pick(
  'You are in. Welcome!',
  'Du er med. Velkommen!'
);

/* Validation and error copy */

export const ERROR_FIRST_NAME = pick(
  'First name is required.',
  'Fornavn er påkrevd.'
);
export const ERROR_SURNAME = pick(
  'Surname is required.',
  'Etternavn er påkrevd.'
);
export const ERROR_PHONE = pick(
  'Phone number is required.',
  'Telefonnummer er påkrevd.'
);
export const ERROR_PHONE_INVALID = pick(
  'The phone number does not look right.',
  'Telefonnummeret ser ikke riktig ut.'
);
export const ERROR_EMAIL = pick('Email is required.', 'E-post er påkrevd.');
export const ERROR_EMAIL_INVALID = pick(
  'The email address does not look right.',
  'E-postadressen ser ikke riktig ut.'
);
export const ERROR_PASSWORD = pick(
  'Password is required.',
  'Passord er påkrevd.'
);
export const ERROR_PASSWORD_WEAK = pick(
  'The password needs at least 8 characters, with at least one digit and one symbol.',
  'Passordet må ha minst 8 tegn, med minst ett tall og ett symbol.'
);
export const ERROR_CONSENT = pick(
  'You need to confirm that you have read the privacy notice.',
  'Du må bekrefte at du har lest personvernerklæringen.'
);
export const ERROR_CODE = pick(
  'Enter the code from the SMS.',
  'Skriv inn koden fra SMS-en.'
);
export const ERROR_CODE_WRONG = pick(
  'The code does not match. Try again.',
  'Koden stemmer ikke. Prøv igjen.'
);
export const ERROR_ACCOUNT_EXISTS = pick(
  'There is already an account with this phone number. Use your password and we sign you in.',
  'Det finnes allerede en konto med dette telefonnummeret. Bruk passordet ditt, så logger vi deg inn.'
);
export const ERROR_SIGNUP = pick(
  'Could not create the account. Try again.',
  'Kunne ikke opprette konto. Prøv igjen.'
);
export const ERROR_CONFIRM = pick(
  'Could not confirm the account. Try again.',
  'Kunne ikke bekrefte kontoen. Prøv igjen.'
);
export const ERROR_RESEND = pick(
  'Could not send a new code. Try again.',
  'Kunne ikke sende ny kode. Prøv igjen.'
);
export const ERROR_SUBMIT = pick(
  'Could not sign you up. Try again.',
  'Kunne ikke melde deg inn. Prøv igjen.'
);

export const isPasswordStrong = (value: string): boolean =>
  value.length >= 8 &&
  /[0-9]/.test(value) &&
  /[\^$*.[\]{}()?"!@#%&/\\,><':;|_~`=+-]/.test(value);

export const isEmailLike = (value: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

export const splitFullName = (
  fullName: string
): { firstName: string; surname: string } => {
  const trimmed = fullName.trim();
  if (!trimmed) {
    return { firstName: '', surname: '' };
  }
  const [firstName, ...rest] = trimmed.split(/\s+/);
  return { firstName, surname: rest.join(' ') };
};

/**
 * The join code as typed or pasted, kept to the characters a code can have.
 * A pasted join link (".../bli-med/K7PX2M") gives its code.
 */
export const normalizeJoinCode = (raw: string): string => {
  const fromLink = raw.match(/bli-med\/([A-Za-z0-9]{1,6})/);
  const source = fromLink ? fromLink[1] : raw;
  return source
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, JOIN_CODE_LENGTH);
};
